import type { PermissionRequirement } from '../role-profile'

export const BUSINESS_TABLE_ACTION_PERMISSIONS = Object.freeze({
  viewTableDetail: ['business_table_view', 'business'],
  accountingBook: 'business_accounting',
  manageAccountingBook: 'business_accounting_modify',
  openTable: 'business_table_open',
  editTableOrder: 'business_table_update_order',
  placeProduct: 'business_table_place_product',
  placeTemporaryProduct: 'business_table_place_temporaryproduct',
  giftProduct: 'business_table_send',
  previewAmount: 'business_table_fee',
  cancelProduct: 'business_table_cancel_product',
  transferProduct: 'business_table_transfer_product',
  transferTable: 'business_table_transfer_table',
  mergeTable: 'business_table_merging',
  pauseTable: 'business_table_pause',
  preCheckout: 'business_table_precheckout',
  cancelOrder: 'business_table_cancel_order',
  settleOrder: ['business_order_settlement', 'business_order_settlement_preview'],
  closeOrder: 'business_order_settlement',
  wholeOrderDiscount: 'business_order_discount',
  roundOrder: 'business_order_change',
  hangOrder: 'business_order_hang',
  memberPay: 'business_order_memberpay',
  tableCommission: 'business_table_place_commission',
  itemCommission: 'business_table_item_commission',
  viewCommissionDetail: [
    'business_table_place_product',
    'dashboard_worker_commission',
    'business_table_place_commission',
    'business_table_item_commission',
  ],
  memberOnTable: 'business_table_member',
  productDiscount: 'business_table_product_discount',
  viewDevice: 'table_device_view',
  controlDevice: 'table_device_control',
}) satisfies Record<string, PermissionRequirement>

export const BUSINESS_BOOKING_ACTION_PERMISSIONS = Object.freeze({
  view: 'business_booking',
  add: 'business_booking_add',
  update: 'business_booking_update',
  cancel: 'business_booking_cancel',
}) satisfies Record<string, PermissionRequirement>

export const PROCUREMENT_ACTION_PERMISSIONS = Object.freeze({
  viewProducts: ['procurement_info', 'procurement_all', 'procurement'],
  useShoppingCart: 'procurement_shoppingcart',
  orderProducts: 'procurement_order',
  manageOrders: 'procurement_manage',
  manageAddress: 'procurement_address',
}) satisfies Record<string, PermissionRequirement>

export const DASHBOARD_REPORT_PERMISSIONS = Object.freeze({
  realtimeData: 'dashboard_info_realtime',
  businessOrders: 'dashboard_business_order',
  sensitiveOperations: 'dashboard_sensitive_view',
  sensitiveAudit: 'dashboard_sensitive_audit',
  billingReports: 'dashboard_business_all',
  productReports: 'dashboard_product',
  tableReports: 'dashboard_table',
  reservationDetails: 'dashboard_reservation_detail',
  inventoryReports: ['dashboard_stock', 'stock', 'stock_info', 'basic_stock', 'basic_stock_info'],
  memberReports: 'dashboard_member',
  operationRecords: 'dashboard_operation_record',
  commissionReports: 'dashboard_worker_commission',
  shiftReports: ['dashboard_business_all', 'business_worker_detail'],
}) satisfies Record<string, PermissionRequirement>

export const DASHBOARD_ACTION_PERMISSIONS = Object.freeze({
  openBusinessOrderDetail: 'dashboard_business_order',
  auditSensitiveTicket: 'dashboard_sensitive_audit',
  exportReports: ['dashboard_export_all', 'dashboard_export'],
  writeOffCreditOrder: 'dashboard_business_2',
  reverseSettlement: 'dashboard_business_1',
  printReceipt: 'business_order_receipt',
  sensitiveAudit: ['dashboard_sensitive_audit'],
  sensitiveConfig: ['dashboard_sensitive_config'],
}) satisfies Record<string, PermissionRequirement>

export const ORDER_ACTION_PERMISSIONS = Object.freeze({
  viewOrders: ['dashboard_business_order', 'business_order', 'business'],
}) satisfies Record<string, PermissionRequirement>

export const SETTING_ACTION_PERMISSIONS = Object.freeze({
  manageProducts: 'setting_product_update',
  manageProductSales: 'setting_product_sale',
  manageProductUnits: 'setting_product_unit',
  manageTables: 'setting_table_update',
  viewTableQrCode: 'setting_table_qrcode',
  manageWorkers: 'setting_worker_bind',
  manageWorkerRoles: 'setting_worker_all',
  manageWorkerCommissionRules: 'setting_worker_rule',
  manageBillingRules: 'setting_bill_rule_all',
  manageOnlineProducts: 'setting_phone_product',
  manageOnlineShop: 'setting_shop_manage',
  manageOnlineBooking: 'setting_shop_order',
  managePaymentMethods: 'setting_payment_mode',
  manageHandover: 'setting_payment_handover',
  managePrinters: 'setting_other_printer',
  manageMessages: 'setting_other_message',
  viewMessageRecords: 'setting_other_message_record_view',
  resendMessages: 'setting_other_message_resend',
  clearData: 'setting_cleardata',
  configureTableDevice: 'table_device_config',
}) satisfies Record<string, PermissionRequirement>

export const MEMBERSHIP_SETTING_ACTION_PERMISSIONS = Object.freeze({
  viewLevels: ['membership_setting_show', 'membership_setting_edit', 'setting_member_level'],
  manageLevels: ['setting_member_level', 'membership_setting_edit'],
  viewChargeRules: ['membership_sale_show', 'membership_setting_show', 'setting_member_rule', 'membership_setting_edit'],
  manageChargeRules: ['setting_member_rule', 'membership_setting_edit'],
}) satisfies Record<string, PermissionRequirement>

export const MEMBERSHIP_ACTION_PERMISSIONS = Object.freeze({
  view: 'membership',
}) satisfies Record<string, PermissionRequirement>

export const SINGLE_STORE_MEMBER_ACTION_PERMISSIONS = Object.freeze({
  view: 'member_solo',
  viewOverview: ['dashboard_member', 'membership'],
  viewMembers: ['membership_info_get_list', 'membership_info_get', 'membership_info', 'membership'],
  viewMemberDetail: ['membership_info_get', 'membership_info'],
  addMember: 'membership_info_add',
  editMember: 'membership_info_update',
  rechargeMember: 'membership_balance_charge',
  manageTags: 'membership_info_update',
  revokeMemberCharge: 'membership_balance_revoke',
  deleteMember: 'membership_delete',
  viewAssets: [
    'dashboard_member',
    'membership_balance',
    'membership_credit_show',
    'membership_setting_show',
    'membership',
  ],
  viewOperations: ['membership_sale_show', 'membership_sale', 'membership_info_get_list', 'membership'],
  viewConsumptionRecords: 'membership_info_consumption',
  viewRechargeRecords: 'membership_balance_charge_records',
  viewOperationRecords: 'membership_info_option',
  manageSmsTemplates: 'membership_sale_sms_manage',
  sendSms: 'membership_sale_sms_send',
  manageRechargeRules: ['setting_member_rule', 'membership_sale_manage', 'membership_setting_edit'],
  viewCreditShop: 'membership_credit_show',
  manageCreditShop: 'membership_credit_manage',
  verifyCreditExchange: 'membership_credit_use',
}) satisfies Record<string, PermissionRequirement>

export const CHAIN_ACTION_PERMISSIONS = Object.freeze({
  view: 'chain',
  viewPurchase: 'chain_purchase',
  viewOperate: 'chain_operate',
  viewMembers: 'chain_member',
  exportMembers: 'chain_member_export',
  manageMemberMarketing: 'chain_member_marketing',
  manageMemberTags: 'chain_member_tag_manage',
  manage: 'chain_manage',
  manageStaff: 'chain_staff_manage',
  inviteStore: 'chain_store_invite',
  proxyPurchase: 'chain_purchase_proxy',
  manageMemberConfig: 'chain_member_config',
}) satisfies Record<string, PermissionRequirement>

export const STOCK_ACTION_PERMISSIONS = Object.freeze({
  viewStock: ['stock_info', 'stock_all', 'stock', 'basic_stock_info', 'basic_stock_all', 'basic_stock'],
  manageStockConfig: ['stock_all', 'basic_stock_all'],
  manageItems: ['stock_object', 'basic_stock_object'],
  manageCostCards: 'stock_cast_card',
  stockIn: ['stock_in', 'basic_stock_in'],
  stockOut: ['stock_out', 'basic_stock_out'],
  stockCheck: ['stock_add_inventory', 'basic_stock_inventory'],
  stockConsume: ['stock_sale', 'basic_stock_in_out', 'basic_stock_info'],
  stockAdjust: ['stock_adjustment', 'basic_stock_modify'],
  stockInOutList: ['basic_stock_in_out', 'stock_in', 'stock_out', 'stock_add_inventory', 'stock_sale'],
  reverseStockIn: ['stock_incounter', 'basic_stock_incounter'],
  reverseStockOut: ['stock_outcounter', 'basic_stock_outcounter'],
  reverseStockCheck: ['stock_add_inventorycounter', 'basic_stock_add_inventorycounter'],
  stockReports: 'dashboard_stock',
}) satisfies Record<string, PermissionRequirement>
